import React, { forwardRef, useMemo, useState, useCallback } from 'react';
import {
  Select,
  SelectProps,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  CircularProgress,
  Chip,
  InputAdornment,
  ListItemText,
  ListItemIcon,
  Divider,
  SelectChangeEvent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS, SAP_TRANSITIONS } from '../../theme/sapDesignSystem';

// Enhanced option interface
export interface SAPSelectOption {
  label: string;
  value: string | number;
  code?: string;
  status?: number;
  disabled?: boolean;
  group?: string;
  description?: string;
  icon?: React.ReactNode;
  metadata?: Record<string, any>;
}

// Component variants using CVA
const selectVariants = cva(
  'transition-all duration-200 ease-in-out',
  {
    variants: {
      variant: {
        outlined: 'border border-gray-300 rounded-md',
        filled: 'bg-gray-50 border-b-2 border-gray-300',
        standard: 'border-b-2 border-gray-300',
      },
      size: {
        xs: 'h-6 text-xs',
        sm: 'h-8 text-sm',
        md: 'h-10 text-base',
        lg: 'h-12 text-lg',
        xl: 'h-14 text-xl',
      },
      state: {
        default: '',
        error: 'border-red-500',
        success: 'border-green-500',
        warning: 'border-yellow-500',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },
    },
    defaultVariants: {
      variant: 'outlined',
      size: 'md',
      state: 'default',
      fullWidth: true,
    },
  }
);

// Enhanced TypeScript interface
export interface SAPSelectProps extends Omit<SelectProps, 'variant' | 'size'> {
  // Custom props
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  state?: 'default' | 'error' | 'success' | 'warning';
  fullWidth?: boolean;
  
  // Enhanced accessibility
  ariaLabel?: string;
  ariaDescribedBy?: string;
  
  // Loading state
  loading?: boolean;
  loadingText?: string;
  
  // Helper text
  helperText?: string;
  helperTextType?: 'info' | 'error' | 'success' | 'warning';
  
  // Icon support
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  
  // Option rendering
  showOptionDescription?: boolean;
  showOptionIcon?: boolean;
  showOptionCode?: boolean;
  showOptionStatus?: boolean;
  
  // Grouping
  groupBy?: (option: SAPSelectOption) => string;
  groupLabelRenderer?: (group: string) => React.ReactNode;
  
  // Multiple selection
  multiple?: boolean;
  chipDisplay?: boolean;
  maxChips?: number;
  
  // Validation
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  required?: boolean;
  
  // Custom styling
  customStyles?: React.CSSProperties;
  optionHeight?: number;
  maxOptions?: number;
  
  // Callbacks
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  onStateChange?: (state: 'default' | 'error' | 'success' | 'warning') => void;
  onOptionSelect?: (option: SAPSelectOption | SAPSelectOption[]) => void;
  
  // Options
  options: SAPSelectOption[];
  placeholder?: string;
}

// Styled components
const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => 
    !['state', 'loading', 'helperTextType', 'customStyles', 'optionHeight'].includes(prop as string),
})<SAPSelectProps>(({ theme, state = 'default', loading, helperTextType, customStyles, optionHeight = 48 }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: SAP_SPACING.component.borderRadius,
    transition: SAP_TRANSITIONS.normal,
    border: `1px solid ${getBorderColor(state)}`,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    fontSize: SAP_TYPOGRAPHY.fontSize.md,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.hover,
      borderColor: getHoverBorderColor(state),
      transform: 'none',
      boxShadow: 'none',
    },
    
    '&.Mui-focused': {
      backgroundColor: SAP_COLORS.sapWhite,
      borderColor: getFocusBorderColor(state),
      boxShadow: `0 0 0 2px ${getFocusShadowColor(state)}`,
      transform: 'translateY(-1px)',
    },
    
    '&.Mui-disabled': {
      backgroundColor: SAP_COLORS.disabled,
      borderColor: SAP_COLORS.sapGray3,
      color: SAP_COLORS.sapGray4,
      cursor: 'not-allowed',
      transform: 'none',
      boxShadow: 'none',
    },
    
    '&.Mui-error': {
      borderColor: SAP_COLORS.sapRed,
      '&:hover': {
        borderColor: SAP_COLORS.sapRed,
      },
      '&.Mui-focused': {
        borderColor: SAP_COLORS.sapRed,
        boxShadow: `0 0 0 2px ${SAP_COLORS.sapLightRed}`,
      },
    },
  },
  
  '& .MuiSelect-select': {
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    fontSize: SAP_TYPOGRAPHY.fontSize.md,
    padding: SAP_SPACING.component.padding,
    lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
    
    ...(loading && {
      color: SAP_COLORS.sapGray4,
      cursor: 'wait',
    }),
  },
  
  '& .MuiInputLabel-root': {
    color: SAP_COLORS.sapGray5,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    fontSize: SAP_TYPOGRAPHY.fontSize.sm,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    
    '&.Mui-focused': {
      color: getFocusLabelColor(state),
      fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    },
    
    '&.Mui-error': {
      color: SAP_COLORS.sapRed,
    },
  },
  
  '& .MuiSelect-icon': {
    color: SAP_COLORS.sapGray5,
    transition: SAP_TRANSITIONS.fast,
    
    '&:hover': {
      color: SAP_COLORS.sapBlue,
    },
  },
  
  '& .MuiChip-root': {
    backgroundColor: SAP_COLORS.sapLightBlue,
    color: SAP_COLORS.sapBlue,
    border: `1px solid ${SAP_COLORS.sapBlue}`,
    borderRadius: SAP_SPACING.component.borderRadius,
    fontSize: SAP_TYPOGRAPHY.fontSize.sm,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    margin: SAP_SPACING.xs,
    
    '& .MuiChip-deleteIcon': {
      color: SAP_COLORS.sapBlue,
      '&:hover': {
        color: SAP_COLORS.sapRed,
      },
    },
  },
  
  ...customStyles,
}));

// Styled MenuItem component
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  minHeight: '48px',
  padding: `${SAP_SPACING.sm} ${SAP_SPACING.md}`,
  borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
  transition: SAP_TRANSITIONS.fast,
  
  '&:hover': {
    backgroundColor: SAP_COLORS.hover,
  },
  
  '&.Mui-selected': {
    backgroundColor: SAP_COLORS.sapBlue,
    color: SAP_COLORS.sapWhite,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.sapDarkBlue,
    },
  },
  
  '&.Mui-disabled': {
    backgroundColor: SAP_COLORS.disabled,
    color: SAP_COLORS.sapGray4,
    cursor: 'not-allowed',
  },
}));

// Group header component
const GroupHeader = styled(Box)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapGray1,
  padding: `${SAP_SPACING.sm} ${SAP_SPACING.md}`,
  borderBottom: `1px solid ${SAP_COLORS.sapGray3}`,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
  fontSize: SAP_TYPOGRAPHY.fontSize.sm,
  color: SAP_COLORS.sapGray6,
  textTransform: 'uppercase',
  letterSpacing: SAP_TYPOGRAPHY.letterSpacing.wide,
}));

// Utility functions
function getBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapGray3;
  }
}

function getHoverBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapGray4; // light grey on hover
  }
}

function getFocusBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapBlue;
  }
}

function getFocusShadowColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapLightRed;
    case 'success': return SAP_COLORS.sapLightGreen;
    case 'warning': return SAP_COLORS.sapLightOrange;
    default: return SAP_COLORS.sapLightBlue;
  }
}

function getFocusLabelColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapBlue;
  }
}

// Status color getter
function getStatusColor(status: number): string {
  switch (status) {
    case 1: return SAP_COLORS.sapGreen;
    case 2: return SAP_COLORS.sapOrange;
    case 3: return SAP_COLORS.sapRed;
    default: return SAP_COLORS.sapGray4;
  }
}

// Default option renderer
const DefaultOptionRenderer = (
  option: SAPSelectOption,
  showDescription: boolean = false,
  showIcon: boolean = false,
  showCode: boolean = false,
  showStatus: boolean = false
) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {showIcon && option.icon && (
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 24 }}>
        {option.icon}
      </Box>
    )}
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
          color: 'inherit',
        }}
      >
        {option.label}
      </Typography>
      {showDescription && option.description && (
        <Typography
          variant="caption"
          sx={{
            color: SAP_COLORS.sapGray5,
            fontSize: SAP_TYPOGRAPHY.fontSize.xs,
            lineHeight: SAP_TYPOGRAPHY.lineHeight.tight,
          }}
        >
          {option.description}
        </Typography>
      )}
    </Box>
    {showCode && option.code && (
      <Chip
        label={option.code}
        size="small"
        sx={{
          fontSize: SAP_TYPOGRAPHY.fontSize.xs,
          height: '20px',
          backgroundColor: SAP_COLORS.sapGray2,
          color: SAP_COLORS.sapGray6,
        }}
      />
    )}
    {showStatus && option.status !== undefined && (
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: getStatusColor(option.status),
        }}
      />
    )}
  </Box>
);

// Main component
export const SAPSelect = forwardRef<HTMLDivElement, SAPSelectProps>(
  (
    {
      variant = 'outlined',
      size = 'md',
      state = 'default',
      fullWidth = true,
      loading = false,
      loadingText = 'Loading options...',
      helperText,
      helperTextType = 'info',
      startIcon,
      endIcon,
      showOptionDescription = false,
      showOptionIcon = false,
      showOptionCode = false,
      showOptionStatus = false,
      groupBy,
      groupLabelRenderer,
      multiple = false,
      chipDisplay = false,
      maxChips = 3,
      validateOnBlur = true,
      validateOnChange = false,
      required = false,
      customStyles,
      optionHeight = 48,
      maxOptions = 50,
      onValidationChange,
      onStateChange,
      onOptionSelect,
      options = [],
      value,
      onChange,
      label,
      placeholder,
      ...props
    },
    ref
  ) => {
    // Local state
    const [isOpen, setIsOpen] = useState(false);

    // Memoized grouped options
    const groupedOptions = useMemo(() => {
      if (!groupBy) return options;
      
      const groups: Record<string, SAPSelectOption[]> = {};
      options.forEach(option => {
        const group = groupBy(option);
        if (!groups[group]) groups[group] = [];
        groups[group].push(option);
      });
      
      return Object.entries(groups).map(([group, options]) => ({
        group,
        options,
      }));
    }, [options, groupBy]);

    // Enhanced change handler
    const handleChange = useCallback((event: SelectChangeEvent<any>, _child?: React.ReactNode) => {
      const selectedValue = event.target.value;
      const selectedOptions = multiple 
        ? options.filter(option => (selectedValue as any[]).includes(option.value))
        : options.find(option => option.value === selectedValue);
      
      onChange?.(event, _child as any);
      onOptionSelect?.(selectedOptions as any);
    }, [onChange, onOptionSelect, options, multiple]);

    // Enhanced accessibility props
    const accessibilityProps = {
      'aria-label': props['aria-label'] || 'Select option',
      'aria-describedby': props['aria-describedby'],
      'aria-busy': loading,
      'aria-expanded': isOpen,
      'aria-haspopup': 'listbox',
    };

    // Render menu items
    const renderMenuItems = () => {
      if (groupBy) {
        return (groupedOptions as Array<{ group: string; options: SAPSelectOption[] }>).map(({ group, options }) => (
          <Box key={group}>
            <GroupHeader>
              {groupLabelRenderer ? groupLabelRenderer(group) : group}
            </GroupHeader>
            {options.map((option: SAPSelectOption) => (
              <StyledMenuItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                sx={{ minHeight: optionHeight }}
              >
                {DefaultOptionRenderer(
                  option,
                  showOptionDescription,
                  showOptionIcon,
                  showOptionCode,
                  showOptionStatus
                )}
              </StyledMenuItem>
            ))}
          </Box>
        ));
      }

      return options.map((option) => (
        <StyledMenuItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          sx={{ minHeight: optionHeight }}
        >
          {DefaultOptionRenderer(
            option,
            showOptionDescription,
            showOptionIcon,
            showOptionCode,
            showOptionStatus
          )}
        </StyledMenuItem>
      ));
    };

    return (
      <Box
        ref={ref}
        sx={{
          position: 'relative',
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        <FormControl
          fullWidth={fullWidth}
          required={required}
          error={state === 'error'}
          disabled={loading}
          sx={{ position: 'relative' }}
        >
          <InputLabel
            sx={{
              color: SAP_COLORS.sapGray5,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
              fontSize: SAP_TYPOGRAPHY.fontSize.sm,
              fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
              
              '&.Mui-focused': {
                color: getFocusLabelColor(state),
                fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              },
              
              '&.Mui-error': {
                color: SAP_COLORS.sapRed,
              },
            }}
          >
            {label}
          </InputLabel>
          
          {/** Avoid passing aria-haspopup as string to Select */}
          {/** eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <StyledSelect
            variant={"outlined" as any}
            size={((size === 'xs' || size === 'sm') ? 'small' : 'medium') as any}
            state={state}
            fullWidth={fullWidth}
            loading={loading}
            helperTextType={helperTextType}
            customStyles={customStyles}
            optionHeight={optionHeight}
            value={value}
            onChange={handleChange}
            label={label}
            multiple={multiple}
            displayEmpty
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            renderValue={(selected: any) => {
              if (multiple) {
                const selectedOptions = options.filter(option => 
                  (selected as any[]).includes(option.value)
                );
                
                if (chipDisplay) {
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selectedOptions.slice(0, maxChips).map((option) => (
                        <Chip
                          key={option.value}
                          label={option.label}
                          size="small"
                          sx={{
                            backgroundColor: SAP_COLORS.sapLightBlue,
                            color: SAP_COLORS.sapBlue,
                            border: `1px solid ${SAP_COLORS.sapBlue}`,
                          }}
                        />
                      ))}
                      {selectedOptions.length > maxChips && (
                        <Chip
                          label={`+${selectedOptions.length - maxChips}`}
                          size="small"
                          sx={{
                            backgroundColor: SAP_COLORS.sapGray2,
                            color: SAP_COLORS.sapGray6,
                          }}
                        />
                      )}
                    </Box>
                  );
                }
                
                return selectedOptions.map(option => option.label).join(', ');
              }
              
              const selectedOption = options.find(option => option.value === selected);
              return selectedOption?.label || placeholder || 'Select an option';
            }}
            startAdornment={startIcon}
            endAdornment={endIcon}
            {...(accessibilityProps as any)}
            {...props}
          >
            {loading ? (
              <StyledMenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">{loadingText}</Typography>
                </Box>
              </StyledMenuItem>
            ) : options.length === 0 ? (
              <StyledMenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  No options available
                </Typography>
              </StyledMenuItem>
            ) : (
              renderMenuItems()
            )}
          </StyledSelect>
          
          {helperText && (
            <Typography
              variant="caption"
              sx={{
                marginTop: SAP_SPACING.xs,
                fontSize: SAP_TYPOGRAPHY.fontSize.xs,
                fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
                lineHeight: SAP_TYPOGRAPHY.lineHeight.tight,
                
                ...(helperTextType === 'error' && {
                  color: SAP_COLORS.sapRed,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                }),
                
                ...(helperTextType === 'success' && {
                  color: SAP_COLORS.sapGreen,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                }),
                
                ...(helperTextType === 'warning' && {
                  color: SAP_COLORS.sapOrange,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                }),
                
                ...(helperTextType === 'info' && {
                  color: SAP_COLORS.sapGray5,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
                }),
              }}
            >
              {helperText}
            </Typography>
          )}
        </FormControl>
      </Box>
    );
  }
);

// Display name for debugging
SAPSelect.displayName = 'SAPSelect';

// Export types for external use
export { selectVariants };
